import { useEffect, useState, useCallback } from 'react';
import { ArrowLeft, Loader2, CheckCircle2, AlertCircle, Copy, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CopyBtn({ text }) {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setOk(true);
          setTimeout(() => setOk(false), 1500);
        } catch (e) {}
      }}
      className="ml-2 text-white/40 hover:text-neon-red transition-colors inline-flex align-middle shrink-0"
      title="Copiar"
    >
      {ok ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
    </button>
  );
}

// Render a value as text (non-recursive; nested objects/arrays are pretty-printed JSON)
function renderValue(v) {
  if (v === null || v === undefined || v === '') {
    return <span className="text-white/40 italic">—</span>;
  }
  if (typeof v === 'string') {
    const stringify = String(v);
    return (
      <div className="font-jetbrains text-sm text-white whitespace-pre-wrap break-words">
        {stringify}
        <CopyBtn text={stringify} />
      </div>
    );
  }
  if (typeof v === 'number' || typeof v === 'boolean') {
    return <span className="font-jetbrains text-sm text-white">{String(v)}</span>;
  }
  // Objects and arrays: pretty-printed JSON block
  const pretty = JSON.stringify(v, null, 2);
  return (
    <div className="relative">
      <pre className="font-jetbrains text-xs text-white whitespace-pre-wrap break-words overflow-x-auto max-h-96 leading-relaxed">
        {pretty}
      </pre>
      <div className="absolute top-1 right-1">
        <CopyBtn text={pretty} />
      </div>
    </div>
  );
}

function Section({ title, value }) {
  if (value === undefined || value === null) return null;
  return (
    <div className="mb-5">
      <h4 className="font-jetbrains text-[10px] uppercase tracking-widest text-neon-red mb-2">
        {title}
      </h4>
      <div className="bg-black/40 border border-neon-red/15 p-3">
        {renderValue(value)}
      </div>
    </div>
  );
}

const JobDetail = ({ jobId, onBack, onRefresh }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/jobs/${jobId}`, { credentials: 'include' });
      if (resp.ok) {
        const d = await resp.json();
        setJob(d.job);
      }
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!job) return;
    if (job.status === 'pending' || job.status === 'running') {
      const t = setInterval(load, 3000);
      return () => clearInterval(t);
    }
  }, [job, load]);

  if (loading || !job) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-neon-red animate-spin" />
      </div>
    );
  }

  const d = job.deliverables || {};
  const isCompleted = job.status === 'completed';
  const isFailed = job.status === 'failed';
  const isRunning = job.status === 'running' || job.status === 'pending';

  const sections = [
    ['Concepto', d.concept],
    ['Objetivo de aprendizaje', d.learning_goal],
    ['Tema', d.topic],
    ['Persona seleccionada', d.selected_subject],
    ['Candidatos considerados', d.candidates_considered],
    ['Hook', d.hook],
    ['Guion', d.script],
    ['Personajes', d.characters],
    ['Storyboard', d.storyboard],
    ['Prompts de imagen', d.image_prompts],
    ['Prompts de video', d.video_prompts],
    ['Recursos visuales', d.visual_assets_needed],
    ['Narración', d.narration],
    ['Música y efectos', d.music_and_sfx],
    ['Estructura de edición', d.editing_structure],
    ['Subtítulos', d.captions],
    ['Hechos confirmados', d.facts],
    ['No confirmado / disputado', d.unconfirmed_or_disputed],
    ['Fuentes', d.sources],
    ['Publicación', d.publication],
    ['Notas de revisión', d.review_notes],
  ];

  return (
    <div data-testid="job-detail">
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-4 font-jetbrains text-xs text-white/60 hover:text-white transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>

      <div className="mb-5 p-4 border border-neon-red/20 bg-black/40">
        <p className="font-outfit text-lg font-bold text-white mb-1">
          {job.task_description}
        </p>
        <p className="font-jetbrains text-xs text-white/50 uppercase tracking-wider">
          {job.agent_id} · {new Date(job.created_at).toLocaleString('es-ES')}
        </p>
        <div className="mt-3 flex items-center gap-3">
          {isRunning && <Loader2 className="w-4 h-4 text-neon-red animate-spin" />}
          {isCompleted && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
          {isFailed && <AlertCircle className="w-4 h-4 text-red-400" />}
          <span className="font-jetbrains text-xs text-white">
            {job.progress_message} {isRunning && `(${job.progress}%)`}
          </span>
        </div>
        {job.error && (
          <p className="mt-3 font-jetbrains text-xs text-red-400 break-words">
            {job.error}
          </p>
        )}
      </div>

      {isCompleted && sections.map(([title, val]) => (
        <Section key={title} title={title} value={val} />
      ))}
    </div>
  );
};

export default JobDetail;
